import {
    Menu,
    menuClasses,
    MenuItem,
    MenuProps,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material'
import { FC, MouseEvent, useLayoutEffect, useState } from 'react'
import { useInvalidateReplyScheduleCalendarQuery } from '../../../../api/hooks/useInvalidateReplyScheduleCalendarQuery'
import { useInvalidateThreadListQuery } from '../../../../api/hooks/useInvalidateThreadListQuery'
import CalendarAdd from '../../../../assets/icons/CalendarAdd.svg?react'
import CalendarArrowUp from '../../../../assets/icons/CalendarArrowUp.svg?react'
import { ProjectSelect } from '../../../../components/ProjectSelect/ProjectSelect'
import { FREE_MODEL_BY_DEFAULT } from '../../../../constants/constants'
import { MeetCreateDialog } from '../../../../dialogs/MeetCreateDialog'
import {
    RegularRequestCreateDialog,
    RegularRequestCreateDialogData,
} from '../../../../dialogs/RegularRequestCreateDialog'
import { useProfileStore } from '../../../../store/storeProfile'
import { useRequestsStore } from '../../../../store/storeRequests'
import { useWrapperStore } from '../../../../store/storeWrapper'
import { ModelSelect } from './ModelSelect'

type Props = MenuProps & {
    selectedDate?: Date
}

export const CreateEventMenu: FC<Props> = ({ selectedDate, ...menuProps }) => {
    const { breakpoints } = useTheme()

    const { isFullAccess, userTariff } = useProfileStore()
    const {
        setCurrentAgentAndAssistantIdDialog,
        fetchModelDetail,
        currentAssistantIdDialog,
        currentAgentNameInDialog,
        modelDetail,
        isTempUser,
    } = useWrapperStore((state) => state)
    const {
        createRequest,
        requestSettingAdditionalContexts,
        requestSettingParams,
        requestMemoryMessageCount,
        requestSettingRagEnabled,
        requestSettingVoiceAnswer,
        requestSettingContext,
        requestSettingLlmTools,
    } = useRequestsStore((state) => state)

    const isMobile = useMediaQuery(breakpoints.down('md'))

    const invalidateThreadListQuery = useInvalidateThreadListQuery()
    const invalidateRequests = useInvalidateReplyScheduleCalendarQuery()

    const [regularRequestDialogData, setRegularRequestDialogData] =
        useState<RegularRequestCreateDialogData | null>(null)
    const [projectId, setProjectId] = useState<string | undefined>(undefined)

    const [createMeetDialogData, setCreateMeetDialogData] = useState<Date | undefined>()

    const handleSubmitRegularRequest = () => {
        invalidateThreadListQuery()
        invalidateRequests()
    }

    const handleCloseRegularRequestCreateDialog = () => {
        setRegularRequestDialogData(null)
    }

    const handleCreateThread = async (name: string) => {
        return await createRequest({
            workplace_id: projectId,
            agent: currentAgentNameInDialog,
            name,
            assistant_id: currentAssistantIdDialog,
            memory_message_count: requestMemoryMessageCount,
            rag_enabled: requestSettingRagEnabled ?? true,
            voice_answer: requestSettingVoiceAnswer?.voiceAnswer ?? false,
            voice_speed: requestSettingVoiceAnswer?.voiceSpeed,
            speech_voice: requestSettingVoiceAnswer?.speechVoice,
            params: requestSettingParams,
            additional_contexts: requestSettingAdditionalContexts,
            context: requestSettingContext ?? null,
            llm_tools: requestSettingLlmTools ?? null,
            min_balance: modelDetail?.is_free ? 0 : 1,
        })
    }

    useLayoutEffect(() => {
        const currentAgentNameInDialog = isFullAccess ? 'AUTO' : FREE_MODEL_BY_DEFAULT

        setCurrentAgentAndAssistantIdDialog({
            agent: currentAgentNameInDialog,
            assistantId: undefined,
        })
        fetchModelDetail(currentAgentNameInDialog, undefined)
    }, [isFullAccess])

    const menuItem = [
        {
            key: 'regular-request',
            label: 'Регулярный запрос',
            icon: <CalendarArrowUp color="#006CFD" width="16px" height="16px" />,
            onClick: (e: MouseEvent<HTMLLIElement>) => {
                menuProps.onClose?.(e, 'escapeKeyDown')
                setRegularRequestDialogData({
                    text: '',
                    files: [],
                    startDate: selectedDate,
                })
            },
        },
        {
            key: 'meet',
            label: 'Добавить бота к встрече',
            icon: <CalendarAdd color="#229F0A" width="16px" height="16px" />,
            onClick: (e: MouseEvent<HTMLLIElement>) => {
                menuProps.onClose?.(e, 'escapeKeyDown')
                setCreateMeetDialogData(selectedDate)
            },
        },
    ]

    return (
        <>
            <RegularRequestCreateDialog
                open={!!regularRequestDialogData}
                initialData={regularRequestDialogData}
                onClose={handleCloseRegularRequestCreateDialog}
                onSubmit={handleSubmitRegularRequest}
                handleCreateThread={handleCreateThread}
                additionalContent={
                    <Stack
                        direction={isMobile ? 'column' : 'row'}
                        alignItems="center"
                        justifyContent="space-between"
                        pb={3}
                        gap={isMobile ? 2 : 3}>
                        <Stack direction="column" gap={1} width={isMobile ? '100%' : '50%'}>
                            <Typography variant="h5" color="#000000">
                                Выбор проекта
                            </Typography>
                            <ProjectSelect
                                id={projectId}
                                handleChange={(_, value) => setProjectId(value)}
                            />
                        </Stack>
                        <Stack direction="column" gap={1} width={isMobile ? '100%' : '50%'}>
                            <Typography variant="h5" color="#000000">
                                Выбор модели
                            </Typography>
                            <ModelSelect />
                        </Stack>
                    </Stack>
                }
            />
            <MeetCreateDialog
                open={!!createMeetDialogData}
                initialDate={createMeetDialogData}
                handleClose={() => setCreateMeetDialogData(undefined)}
            />
            <Menu
                {...menuProps}
                disableEnforceFocus
                disableAutoFocus
                disableAutoFocusItem
                sx={{
                    [`.${menuClasses.paper}`]: {
                        width: 240,
                        borderRadius: '12px',
                        border: '1px solid #E9EAED',
                        boxShadow: 'none',
                        overflow: 'hidden',
                    },
                    [`.${menuClasses.list}`]: {
                        p: '8px',
                        maxHeight: 360,
                        overflow: 'auto',
                    },
                }}>
                {menuItem.map(({ key, label, icon, onClick }) => (
                    <MenuItem
                        disabled={
                            key === 'meet' &&
                            (isTempUser ||
                                !isFullAccess ||
                                !userTariff?.tariff?.restrictions?.is_recording_available)
                        }
                        key={key}
                        onClick={onClick}
                        sx={{
                            height: '36px',
                            minHeight: 'unset',
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 1,
                            p: '8px',
                            color: '#181B25',
                        }}>
                        {icon}
                        <Typography
                            variant="body2"
                            color="#181B25"
                            sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}>
                            {label}
                        </Typography>
                    </MenuItem>
                ))}
            </Menu>
        </>
    )
}
